import React, { useEffect, useState, useCallback } from "react";
import { jwtDecode } from "jwt-decode"; // Import jwt-decode
import LoadingScreen from '../../Admin/Loading_Screen/LoadingScreen';
import API_BASE_URL from '../../ApiConfig/ApiConfig';
import Cookies from "js-cookie";

const UserScheme = () => {
  const [schemes, setSchemes] = useState([]);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [invoiceFiles, setInvoiceFiles] = useState({});
  const [customerId, setCustomerId] = useState(null);
  const [customerEmail, setCustomerEmail] = useState(null);
  const [purchaseDate, setPurchaseDate] = useState({});
  const [purchaseFrom, setPurchaseFrom] = useState({});
  const [invoiceAmount, setInvoiceAmount] = useState({});
  const token = Cookies.get("token");

  // Retrieve and decode the token
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setCustomerId(decodedToken.customerId);
        setCustomerEmail(decodedToken.email);
        console.log("Customer ID:", decodedToken.customerId);
        console.log("Customer Email:", decodedToken.email);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    } else {
      console.log("No token found in local storage.");
    }
  }, [token]);

  const fetchSchemes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/Customer-Scheme/all`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });
      const data = await response.json();
      console.log("Fetched schemes data:", data);

      // Filter only live schemes
      const currentDate = new Date();
      const liveSchemes = data.filter(
        (scheme) => scheme.LiveScheme === true
      );

      // Sort schemes by name
      liveSchemes.sort((a, b) => a.schemeName.localeCompare(b.schemeName));

      setSchemes(liveSchemes);
      setLoading(false);
    } catch (err) {
      setError("Error fetching schemes");
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (customerId) {
      fetchSchemes();
    } else {
      console.log("Customer ID is not set");
    }
  }, [customerId, fetchSchemes]);

  // Check if scheme is expired
  const isSchemeExpired = (scheme) => {
    const currentDate = new Date();
    const endDate = new Date(scheme.schemeEndDate);
    // Set end date to end of day (23:59:59)
    endDate.setHours(23, 59, 59, 999);
    return currentDate > endDate;
  };

  // Handle scheme selection
  const handleSelectScheme = (schemeId) => {
    if (selectedSchemes.includes(schemeId)) {
      setSelectedSchemes([]);
      setInvoiceFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[schemeId];
        return newFiles;
      });
    } else {
      setSelectedSchemes([schemeId]); // Allow only one scheme to be selected
    }
  };

  // Handle "Next" button click
  const handleNextClick = () => {
    if (selectedSchemes.length > 0) {
      setShowDetails(true);
    }
  };

  // Handle invoice upload
  const handleInvoiceUpload = (e, schemeId) => {
    const file = e.target.files[0];
    setInvoiceFiles((prev) => ({
      ...prev,
      [schemeId]: file,
    }));
  };

  const handlePurchaseDateChange = (e, schemeId) => {
    setPurchaseDate((prev) => ({
      ...prev,
      [schemeId]: e.target.value,
    }));
  };

  const handlePurchaseFromChange = (e, schemeId) => {
    setPurchaseFrom((prev) => ({
      ...prev,
      [schemeId]: e.target.value,
    }));
  };

  const handleInvoiceAmountChange = (e, schemeId) => {
    setInvoiceAmount((prev) => ({
      ...prev,
      [schemeId]: e.target.value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (!termsAccepted) {
      alert("You must agree to the terms and conditions.");
      return;
    }
    if (selectedSchemes.some((schemeId) => !invoiceFiles[schemeId])) {
      alert("Please upload an invoice for each selected scheme.");
      return;
    }
    if (!customerId) {
      alert("Unable to retrieve customer ID. Please login again.");
      return;
    }

    const formData = new FormData();
    formData.append("customerId", customerId);
    formData.append("customerEmail", customerEmail);

    selectedSchemes.forEach((schemeId) => {
      const scheme = schemes.find((s) => s.schemeId === schemeId);
      if (!scheme) {
        alert(`Scheme with ID ${schemeId} not found.`);
        return;
      }
      formData.append(`schemeIds[]`, schemeId);
      formData.append(`schemeNames[]`, scheme.schemeName);
      formData.append(`schemeDates[]`, scheme.schemeEndDate); // Use the correct field name
      formData.append(`invoiceFiles[]`, invoiceFiles[schemeId]);
      formData.append(`purchaseDates[]`, purchaseDate[schemeId]);
      formData.append(`purchaseFrom[]`, purchaseFrom[schemeId]);
      formData.append(`invoiceAmounts[]`, invoiceAmount[schemeId]);
    });

    try {
      const response = await fetch(`${API_BASE_URL}/Customer-Scheme/create`, {
        method: "POST",
        body: formData,
        headers: {
          "Authorization": token ? `Bearer ${token}` : "", // Token placed correctly in headers
        },
      });
      const result = await response.json();
      if (response.ok) {
        alert(`Invoice submitted successfully!`);

        // Clear the selected schemes and invoice files
        setSelectedSchemes([]);
        setInvoiceFiles({});
        setShowDetails(false);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  if (error) {
    return <p className="text-center text-red-500">{error}</p>;
  }

  return (
    <div
      className="min-w-full mx-auto md:mt-20 bg-white rounded-2xl shadow-md overflow-hidden"
      style={{ boxShadow: '0px 10px 60px 0px #E2ECF980' }}
    >
      <div className="p-4">
        <h1 className="text-xl md:text-2xl font-bold text-[#292D32] mb-2">Select Schemes</h1>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : schemes.length === 0 ? (
        <p className="text-center text-red-500 my-10 md:my-20">Scheme not available</p>
      ) : (
        <div className="overflow-x-auto">
          {/* Desktop Table */}
          <table className="w-full bg-white rounded-md border-collapse hidden md:table">
            <thead className="bg-[#1C50A3] text-[#FFFFFF]">
              <tr>
                <th className="py-3 px-6 border-b font-semibold text-left">Select</th>
                <th className="py-3 px-6 border-b font-semibold text-left">Scheme Name</th>
                <th className="py-3 px-6 border-b font-semibold text-left">Scheme ID</th>
                <th className="py-3 px-6 border-b font-semibold text-left">Scheme End Date</th>
                <th className="py-3 px-6 border-b font-semibold text-left">Status</th>
              </tr>
            </thead>

            <tbody>
              {schemes.map((scheme) => {
                const expired = isSchemeExpired(scheme);
                return (
                  <tr key={scheme.schemeId} className={`hover:bg-gray-100 transition ${expired ? 'bg-gray-50' : 'cursor-pointer'}`}>
                    <td className="py-3 px-6 border-b text-left">
                      <input
                        type="checkbox"
                        checked={selectedSchemes.includes(scheme.schemeId)}
                        onChange={() => handleSelectScheme(scheme.schemeId)}
                        className="mr-2 h-4 w-4 text-indigo-600 accent-[#006CB5] border-gray-300 rounded focus:outline-none"
                      />
                    </td>
                    <td className="py-3 px-6 border-b text-left text-gray-600 text-[14px] font-semibold">
                      {scheme.schemeName}
                    </td>
                    <td className="py-3 px-6 border-b text-left text-gray-600 text-[14px] font-semibold">
                      {scheme.schemeId}
                    </td>
                    <td className="py-3 px-6 border-b text-left text-gray-600 text-[14px] font-semibold">
                      {new Date(scheme.schemeEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </td>
                    <td className="py-3 px-6 border-b text-left text-gray-600 text-[14px] font-semibold">
                      {expired ? (
                        <span className="text-red-500">Scheme is over - only upload invoice</span>
                      ) : (
                        <span className="text-green-500">Active</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Mobile Table */}
          <div className="md:hidden">
            {schemes.map((scheme) => {
              const expired = isSchemeExpired(scheme);
              return (
                <div key={scheme.schemeId} className={`p-4 border-b ${expired ? 'bg-gray-50' : 'hover:bg-gray-100 transition cursor-pointer'}`}>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-semibold">Select</span>
                    {!expired ? (
                      <input
                        type="checkbox"
                        checked={selectedSchemes.includes(scheme.schemeId)}
                        onChange={() => handleSelectScheme(scheme.schemeId)}
                        className="h-4 w-4 text-indigo-600 accent-[#006CB5] border-gray-300 rounded focus:outline-none"
                      />
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600 font-semibold">Scheme Name</span>
                    <span className="text-gray-600">{scheme.schemeName}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600 font-semibold">Scheme ID</span>
                    <span className="text-gray-600">{scheme.schemeId}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600 font-semibold">Scheme End Date</span>
                    <span className="text-gray-600">
                      {new Date(scheme.schemeEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600 font-semibold">Status</span>
                    <span className={expired ? 'text-red-500' : 'text-green-500'}>
                      {expired ? 'Scheme is over - only upload invoice' : 'Active'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6 md:py-8">
        <button
          onClick={handleNextClick}
          disabled={selectedSchemes.length === 0}
          className={`mt-4 px-4 py-2 rounded bg-[#1C50A3] text-white font-bold ${selectedSchemes.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          Next
        </button>

        {/* Modal for Details Section */}
        {showDetails && (
          <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-2xl shadow-lg w-11/12 md:w-1/2 p-4 md:p-6">
              <div className="p-1 pr-4 overflow-y-auto max-h-[80vh] custom-scrollbar">
                <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-[#292D32]">Invoices Sections</h1>

                {/* Grid layout to display one item per row on mobile */}
                <div className="grid grid-cols-1 gap-4">
                  {selectedSchemes.map((schemeId) => {
                    const scheme = schemes.find((s) => s.schemeId === schemeId);
                    const expired = isSchemeExpired(scheme);
                    return (
                      <div key={schemeId} className="mb-4 p-4 border rounded-lg shadow-sm bg-[#F4F4F4]">
                        <h2 className="text-lg md:text-xl text-[#292D32] font-bold mb-2">{scheme.schemeName}</h2>
                        {expired && (
                          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-2 mb-2">
                            <p className="font-bold">Note:</p>
                            <p>You can only upload invoices for reference.</p>
                          </div>
                        )}
                        <p className="font-light">
                          <strong className="font-medium">Scheme ID:</strong> {scheme.schemeId}
                        </p>
                        <p className="font-light">
                          <strong className="font-medium">Scheme End Date:</strong>{" "}
                          {new Date(scheme.schemeEndDate).toLocaleDateString()}
                        </p>
                        <label className="block mt-4">
                          <span className="text-gray-700 font-bold">Upload Invoice</span>
                          <div className="relative mt-2 flex items-center justify-center border-2 border-dashed border-gray-300 p-2 cursor-pointer">
                            <span className="text-gray-600 font-semibold">Upload Invoice</span>
                            <input
                              type="file"
                              onChange={(e) => handleInvoiceUpload(e, schemeId)}
                              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                            />
                            <span className="absolute left-10 top-1/2 transform -translate-y-1/2">
                              <img
                                loading="lazy"
                                src="https://cdn.builder.io/api/v1/image/assets/TEMP/1188cead5b5354fce8fccad17d436396ee4d4ab7391a6d465632407d691f8e86?placeholderIfAbsent=true&apiKey=208ba67bf72b486f9889b172fa1cace6"
                                alt="Upload Icon"
                                className="object-contain w-full max-w-[28px] aspect-[1.56]"
                              />
                            </span>
                          </div>
                        </label>
                        <form>
                          <label className="block mt-4">
                            <span className="text-gray-700 font-bold">Date of Purchase</span>
                            <input
                              type="date"
                              value={purchaseDate[schemeId] || ""}
                              onChange={(e) => handlePurchaseDateChange(e, schemeId)}
                              className="mt-2 p-2 border rounded w-full"
                            />
                          </label>
                          <label className="block mt-4">
                            <span className="text-gray-700 font-bold">Purchase From</span>
                            <select
                              value={purchaseFrom[schemeId] || ""}
                              onChange={(e) => handlePurchaseFromChange(e, schemeId)}
                              className="mt-2 p-2 border rounded w-full"
                            >
                             <option value="">Select Your Dealer</option>
<option value="ALL_FORTECH">ALL FORTECH</option>
<option value="COMPUTER_PROFESSIONAL">COMPUTER PROFESSIONAL</option>
<option value="COMTECH_SOLUTION_POINT">COMTECH SOLUTION POINT</option>
<option value="CREATIVE_IT_HUB">CREATIVE IT HUB</option>
<option value="DIGITAL_EQUIPMENTS">DIGITAL EQUIPMENTS</option>
<option value="DOCKET_CARE_SYSTEMS">DOCKET CARE SYSTEMS</option>
<option value="FORTECH">FORTECH</option>
<option value="GROWTH_PARTNERS">GROWTH PARTNERS</option>
<option value="HITECH_SOLUTIONS">HITECH SOLUTIONS</option>
<option value="JALAN_DISTRIBUTORS">JALAN DISTRIBUTORS</option>
<option value="MICRO_INFOSOLUTIONS">MICRO INFOSOLUTIONS PRIVATE LIMITED</option>
<option value="PIONEER_COMPUTER">PIONEER COMPUTER</option>
<option value="PRANSH_DIGITAL_PRIVATE_LIMITED">PRANSH DIGITAL PRIVATE LIMITED</option>
<option value="SATYA_IT_SOLUTION">SATYA IT SOLUTION</option>
<option value="SRK_ENTERPRISES">SRK ENTERPRISES</option>
<option value="STEK SYSTEM">STEK SYSTEM</option>
<option value="UNIVERSAL">UNIVERSAL</option>
<option value="VISION_INDIA">VISION INDIA</option>




                            </select>
                          </label>
                          <label className="block mt-4">
                            <span className="text-gray-700 font-bold">Invoice Amount</span>
                            <input
                              type="number"
                              value={invoiceAmount[schemeId] || ""}
                              onChange={(e) => handleInvoiceAmountChange(e, schemeId)}
                              className="mt-2 p-2 border rounded w-full"
                            />
                          </label>
                        </form>
                      </div>
                    );
                  })}
                </div>

                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-[#292D32]">Terms & Conditions</h2>
                  <ul className="list-disc pl-5 mt-2 space-y-2 text-gray-600">
                    <li>After claiming the schemes, it will be first verified by the account department.</li>
                    <li>Scheme is time bound and can be submitted only one time.</li>
                    <li>Claiming duration of scheme is time bound; after the time limit, the scheme will be invalid.</li>
                    <li>Scheme Id and other details will be forwarded to your email id after claiming.</li>
                  </ul>
                  <label className="block mt-4">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mr-2"
                    />
                    I agree with the terms and conditions listed above before claiming the scheme.
                  </label>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!termsAccepted || selectedSchemes.some((schemeId) => !invoiceFiles[schemeId])}
                  className={`mt-4 px-4 py-2 rounded bg-[#1C50A3] text-white font-bold ${!termsAccepted ||
                    selectedSchemes.some((schemeId) => !invoiceFiles[schemeId])
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-sky-700"
                    }`}
                >
                  Submit
                </button>

                {/* Close Button */}
                <button
                  onClick={() => setShowDetails(false)}
                  className="mt-4 ml-4 px-4 py-2 rounded bg-gray-300 text-gray-800 font-bold hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserScheme;